import React from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Button from '../Button';

/**
 * Standardized full-page create/edit form shell. Replaces long modals:
 * back link + title row, the form fields inside a card (use className
 * "form-grid" on a wrapper to get the responsive two-column layout),
 * and a fixed action footer (Cancel / Save).
 *
 * @param {object}          props
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.subtitle]
 * @param {string}          [props.backTo]        - route for Back/Cancel (default: -1 history)
 * @param {React.ReactNode} props.children        - Form element with fields
 * @param {() => void}      props.onSubmit
 * @param {boolean}         [props.submitting]
 * @param {string}          [props.submitText='Save']
 * @param {React.ReactNode} [props.footerExtra]   - extra content on the footer's left side
 */
const FormPage = ({
  title,
  subtitle,
  backTo,
  children,
  onSubmit,
  submitting,
  submitText = 'Save',
  footerExtra,
}) => {
  const navigate = useNavigate();
  const goBack = () => (backTo ? navigate(backTo) : navigate(-1));

  return (
    <div className="form-page">
      <button type="button" className="form-page__back" onClick={goBack}>
        <ArrowLeftOutlined /> Back
      </button>

      <div className="page-header form-page__header">
        <div className="page-header__text">
          <h1 className="page-header__title">{title}</h1>
          {subtitle && <div className="page-header__subtitle">{subtitle}</div>}
        </div>
      </div>

      <div className="form-page__card">{children}</div>

      <div className="form-page__footer">
        <div className="form-page__footer-extra">{footerExtra}</div>
        <div className="form-page__footer-actions">
          <Button variant="secondary" onClick={goBack} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={submitting}>
            {submitText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormPage;
